import { tracedFetch } from "./TracedFetch";
import { logger } from "../observability/LoggerFactory";

type EventHandler<T = unknown> = (data: T) => void;

export interface PostSseClientOptions {
  url: string;
  body: unknown;
  events: Record<string, EventHandler>;
  // Отсутствует, когда вызывающему нечего показать на сбое потока: поток тогда
  // просто обрывается, а экран остаётся в состоянии, до которого дошёл.
  onError?: (error: Error) => void;
}

export class PostSseClient {
  private abortController: AbortController | null = null;
  private closed = false;

  public constructor(options: PostSseClientOptions) {
    void this.start(options);
  }

  public close(): void {
    this.closed = true;
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  private async start(options: PostSseClientOptions): Promise<void> {
    this.abortController = new AbortController();

    try {
      const response = await tracedFetch(options.url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(options.body),
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      for (;;) {
        const { done, value } = await reader.read();
        if (done || this.closed) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop()!;

        for (const block of parts) {
          const event = PostSseClient.parseBlock(block);
          if (event) {
            const handler = options.events[event.type];
            if (handler) {
              handler(event.data);
            }
          }
        }
      }
    } catch (error) {
      if (this.closed) {
        return;
      }
      const err =
        error instanceof Error ? error : new Error(String(error));
      logger.error({ err, component: "PostSseClient" }, "POST SSE stream failed");
      if (options.onError !== undefined) {
        options.onError(err);
      }
    }
  }

  private static parseBlock(block: string): { type: string; data: unknown } | null {
    let blockOutcome: { type: string; data: unknown } | null;
    let eventType = "";
    let dataStr = "";
    for (const line of block.split("\n")) {
      if (line.startsWith("event: ")) {
        eventType = line.slice(7);
      } else if (line.startsWith("data: ")) {
        dataStr = line.slice(6);
      }
    }
    if (!eventType || !dataStr) {
      blockOutcome = null;
    } else {
      blockOutcome = { type: eventType, data: JSON.parse(dataStr) };
    }
    return blockOutcome;
  }
}
